import { useCallback, useRef, useState } from 'react'

import { FormHandles, Scope } from '@unform/core'
import { Search } from '@vtex/shoreline'
import debounce from 'lodash/debounce'

import Panel from '~/components/Panel'
import { FormGroup, Radio } from '~/components/unform'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import SubpublishersRadioList from '~/modules/retailMedia/components/SubpublishersRadioList'

import { useSubpublishers } from '../../../../hooks/useSubpublishers'
import { useCampaignForm } from '../../hooks/useCampaignForm'

const DESTINATION_FIELD_NAMES = ['url', 'brand', 'publisher_id'] as const

const clearRegisteredDestinationFields = (form?: FormHandles | null) => {
  const destinations = form?.getData()?.settings?.destinations

  if (!destinations || typeof destinations !== 'object') return

  Object.keys(destinations).forEach(index => {
    DESTINATION_FIELD_NAMES.forEach(fieldName => {
      form?.clearField(`settings.destinations.${index}.${fieldName}`)
    })
  })
}

const Subpublishers: React.FC = () => {
  const { t } = useAPPTranslation()
  const { superAdmin } = useAppInfo()
  const { formRef, setSelectedSubpublishers } = useCampaignForm()

  const [searchValue, setSearchValue] = useState('')
  const [name, setName] = useState('')

  const handleDebounceSearch = useRef(debounce(setName, 500)).current

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value)
      handleDebounceSearch(e.target.value)
    },
    [handleDebounceSearch]
  )

  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    setName('')
  }, [])

  const handleSelectionTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.currentTarget.value === 'all') {
        clearRegisteredDestinationFields(formRef.current)
        setSelectedSubpublishers([])
      }
    },
    [formRef, setSelectedSubpublishers]
  )

  const { data, isPending, handleChange, allowManageSubpublishers } =
    useSubpublishers({ name })

  /**
   * Handle selects
   */

  const optionsTypes: RadioOptionProps[] = [
    {
      id: `subpublishersCheck_all`,
      value: 'all',
      label: t('rm:campaign.create.Subpublishers.options.all.label'),
      description: t('rm:campaign.create.Subpublishers.options.all.description')
    },
    {
      id: `subpublishersCheck_selected`,
      value: 'selected',
      label: t('rm:campaign.create.Subpublishers.options.selected.label'),
      description: t(
        'rm:campaign.create.Subpublishers.options.selected.description'
      ),
      activeChildren: (
        <div className="d-flex flex-column w-100">
          <Search
            value={searchValue}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            messages={{
              placeholder: t('rm:campaign.subpublishers.search.placeholder')
            }}
            className="mb-3"
          />
          <SubpublishersRadioList
            onChange={handleChange}
            data={data}
            isPending={isPending}
            hasSearchTerm={!!name}
          />
        </div>
      )
    }
  ]

  return (
    (allowManageSubpublishers || superAdmin) && (
      <Panel
        title={t('rm:campaign.create.Subpublishers.title')}
        description={t('rm:campaign.create.Subpublishers.description')}
        isCollapsible
      >
        <Scope path="subpublishers">
          <FormGroup>
            <Radio
              options={optionsTypes}
              name="subpublishersCheck"
              onChange={handleSelectionTypeChange}
            />
          </FormGroup>
        </Scope>
      </Panel>
    )
  )
}

export default Subpublishers
